// Input patches for the HTML change handlers that do more than re-render (the stateless engine cannot know which
// field changed): material selects, connection type, element thickness (particleboard ρk), the max-screws button
// and the "Limita righe/colonne" checkbox.

import type { EngineCatalog } from "./catalog";
import { fromControlValues, toControlValues } from "./controls";
import { createCtx, isThreeS, type EngineCtx, type EngineTexts } from "./ctx";
import { fixed, type MaxRowsCols } from "./layout";
import {
  applyMatClasse,
  PARTICLEBOARD_CLASSI,
  populateMatClasse,
  populateMatSpecie,
  populateMatTipo,
  type ElementN,
} from "./materials";
import type { CalculationInput, ConnectionType } from "./types";

const MATERIAL_KEYS = [
  "element1TimberMaterialId",
  "element1CharacteristicDensity",
  "element1MeanDensity",
  "element2TimberMaterialId",
  "element2CharacteristicDensity",
  "element2MeanDensity",
] as const;

function materialPatch(ctx: EngineCtx, input: CalculationInput): Partial<CalculationInput> {
  const next = fromControlValues(ctx.v, ctx.cat);
  const patch: Partial<CalculationInput> = {};
  for (const k of MATERIAL_KEYS) {
    if (next[k] !== input[k]) (patch as Record<string, unknown>)[k] = next[k];
  }
  return patch;
}

function materialCtx(input: CalculationInput, catalog: EngineCatalog, t: EngineTexts): EngineCtx {
  const ctx = createCtx(toControlValues(input, catalog), catalog, t);
  // option lists of both elements as currently shown
  for (const n of [1, 2] as const) {
    populateMatTipo(ctx, n);
    populateMatSpecie(ctx, n);
    populateMatClasse(ctx, n);
  }
  return ctx;
}

// Change of "Tipo di materiale" / "Specie legnosa" / "Classe" of element n (mat{n}Tipo/Specie/Classe handlers):
// cascades the lower selects (keeping the previous value when still valid) and applies the database densities.
// In legno3 a CLT choice also restricts the other element's types (never three CLT).
export function selectMaterial(
  input: CalculationInput,
  catalog: EngineCatalog,
  n: ElementN,
  change: { tipo: string } | { specie: string } | { classe: string },
  t: EngineTexts,
): Partial<CalculationInput> {
  const ctx = materialCtx(input, catalog, t);
  const v = ctx.v;
  if ("tipo" in change) {
    v[`mat${n}Tipo` as const] = change.tipo;
    populateMatSpecie(ctx, n);
    populateMatClasse(ctx, n);
    applyMatClasse(ctx, n);
    if (isThreeS(v.materiale1)) {
      const o: ElementN = n === 1 ? 2 : 1;
      const prevO = v[`mat${o}Tipo` as const];
      populateMatTipo(ctx, o);
      if (v[`mat${o}Tipo` as const] !== prevO) {
        populateMatSpecie(ctx, o);
        populateMatClasse(ctx, o);
        applyMatClasse(ctx, o);
      }
    }
  } else if ("specie" in change) {
    v[`mat${n}Specie` as const] = change.specie;
    populateMatClasse(ctx, n);
    applyMatClasse(ctx, n);
  } else {
    v[`mat${n}Classe` as const] = change.classe;
    applyMatClasse(ctx, n);
  }
  return materialPatch(ctx, input);
}

// Change of the connection type (#materiale1 handler): element 1 materials re-constrained and their densities
// re-applied; element 2 re-applied only if its type had to change.
export function changeConnectionType(
  input: CalculationInput,
  catalog: EngineCatalog,
  connectionType: ConnectionType,
  t: EngineTexts,
): Partial<CalculationInput> {
  const ctx = createCtx(toControlValues({ ...input, connectionType }, catalog), catalog, t);
  const v = ctx.v;
  populateMatTipo(ctx, 1);
  populateMatSpecie(ctx, 1);
  populateMatClasse(ctx, 1);
  applyMatClasse(ctx, 1);
  const prev2 = v.mat2Tipo;
  populateMatTipo(ctx, 2);
  if (v.mat2Tipo !== prev2) {
    populateMatSpecie(ctx, 2);
    populateMatClasse(ctx, 2);
    applyMatClasse(ctx, 2);
  }
  return { connectionType, ...materialPatch(ctx, input) };
}

// Change of t{n}: particleboards (P4–P7) re-apply the class because ρk depends on the thickness (EN 12369-1); the
// other materials keep their (possibly manual) ρk.
export function changeThickness(
  input: CalculationInput,
  catalog: EngineCatalog,
  n: ElementN,
  thickness: number | null,
  t: EngineTexts,
): Partial<CalculationInput> {
  const thicknessPatch: Partial<CalculationInput> = n === 1 ? { element1Thickness: thickness } : { element2Thickness: thickness };
  const next: CalculationInput = { ...input, ...thicknessPatch };
  const ctx = createCtx(toControlValues(next, catalog), catalog, t);
  if (PARTICLEBOARD_CLASSI.includes(ctx.v[`mat${n}Classe` as const])) applyMatClasse(ctx, n);
  return { ...thicknessPatch, ...materialPatch(ctx, next) };
}

// "Applica righe/colonne" of the max-screws result: rows/cols/offset set and LINKED to the maximum again; with
// automatic distances also s1/s2 (the 3D view should then hide the inadmissible screws once: hideInadmissibleOnce).
export function applyMaxScrewsPatch(best: MaxRowsCols, autoSpacing: boolean): Partial<CalculationInput> {
  return {
    gridRowsLinkedToMax: true,
    gridColsLinkedToMax: true,
    gridOffsetLinkedToMax: true,
    gridRows: best.rows,
    gridCols: best.cols,
    gridOffsetX: fixed(best.offsetX, 1),
    gridOffsetY: fixed(best.offsetY, 1),
    ...(autoSpacing ? { spacingS1: fixed(best.passoAsse1, 0), spacingS2: fixed(best.passoAsse2, 0) } : {}),
  };
}

// "Limita righe/colonne al numero massimo ammissibile": on → rows/cols/offset linked to the maximum again; off →
// pre-filled with the current maximum as a starting point, then UNLINKED (free manual values).
export function limitGridTogglePatch(maxRowsCols: MaxRowsCols | null, checked: boolean): Partial<CalculationInput> {
  if (checked) {
    return { limitGridToMax: true, gridRowsLinkedToMax: true, gridColsLinkedToMax: true, gridOffsetLinkedToMax: true };
  }
  const patch: Partial<CalculationInput> = {
    limitGridToMax: false,
    gridRowsLinkedToMax: false,
    gridColsLinkedToMax: false,
    gridOffsetLinkedToMax: false,
  };
  if (maxRowsCols && maxRowsCols.rows >= 1 && maxRowsCols.cols >= 1) {
    patch.gridRows = maxRowsCols.rows;
    patch.gridCols = maxRowsCols.cols;
    patch.gridOffsetX = fixed(maxRowsCols.offsetX, 1);
    patch.gridOffsetY = fixed(maxRowsCols.offsetY, 1);
  }
  return patch;
}
